import {
  LayoutDashboard,
  Store,
  Package,
  Tag,
  ShoppingCart,
  Users,
  BarChart3,
  Layers,
  ShoppingBag,
  CreditCard,
  FileText,
  Settings,
  Globe,
  DollarSign,
  Map,
  Percent,
  Building,
  Leaf,
  Clock,
  QrCode
} from 'lucide-react'

import { NavigationCategory } from '@/types/navigation'
import { authService } from '@/services/authService'

export const navigationCategories: NavigationCategory[] = [
  {
    id: 'dashboard',
    name: 'Tableau de bord',
    icon: LayoutDashboard,
    items: [
      {
        name: 'Tableau de bord',
        href: '/dashboard',
        icon: LayoutDashboard,
        roles: ['admin', 'merchant']
      }
    ],
    roles: ['admin', 'merchant'],
    collapsedByDefault: true
  },
  {
    id: 'commercial',
    name: 'Gestion commerciale',
    icon: Store,
    items: [
      {
        name: 'Commerçants',
        href: '/merchants',
        icon: Store,
        roles: ['admin']
      },
      {
        name: 'Organisations (Marques Globales)',
        href: '/organizations',
        icon: Building,
        roles: ['admin']
      },
      {
        name: 'Mes Utilisateurs',
        href: '/mes-utilisateurs',
        icon: Users,
        roles: ['admin', 'merchant'],
        requiredPermission: 'admin',
        requiredSpecificPermission: 'manageUsers'
      }
    ],
    roles: ['admin', 'merchant'],
    collapsedByDefault: true
  },
  {
    id: 'catalog',
    name: 'Catalogue',
    icon: Package,
    items: [
      {
        name: 'Produits',
        href: '/products',
        icon: Package,
        roles: ['admin', 'merchant'],
        requiredPermission: 'manager',
        requiredSpecificPermission: 'manageProducts'
      },
      {
        name: 'Offres',
        href: '/offers',
        icon: Tag,
        roles: ['admin', 'merchant'],
        requiredPermission: 'manager',
        requiredSpecificPermission: 'manageOffers'
      },
      {
        name: 'Catégories',
        href: '/categories',
        icon: Layers,
        roles: ['admin']
      }
    ],
    roles: ['admin', 'merchant'],
    collapsedByDefault: true
  },
  {
    id: 'transactions',
    name: 'Transactions',
    icon: ShoppingCart,
    items: [
      {
        name: 'Paniers',
        href: '/baskets',
        icon: ShoppingBag,
        roles: ['admin']
      },
      {
        name: 'Paniers clients',
        href: '/carts',
        icon: Package,
        roles: ['admin']
      },
      {
        name: 'Scanner QR',
        href: '/qr-scanner',
        icon: QrCode,
        roles: ['admin', 'merchant'],
        requiredPermission: 'manager'
      },
      {
        name: 'Réservations',
        href: '/reservations',
        icon: ShoppingCart,
        roles: ['admin', 'merchant'],
        requiredPermission: 'manager'
      },
      {
        name: 'Paiements',
        href: '/payments',
        icon: CreditCard,
        roles: ['admin']
      }
    ],
    roles: ['admin', 'merchant'],
    collapsedByDefault: true
  },
  {
    id: 'users',
    name: 'Utilisateurs',
    icon: Users,
    items: [
      {
        name: 'Utilisateurs',
        href: '/users',
        icon: Users,
        roles: ['admin']
      }
    ],
    roles: ['admin', 'merchant'],
    collapsedByDefault: true
  },
  {
    id: 'analytics',
    name: 'Analytique',
    icon: BarChart3,
    items: [
      {
        name: 'Statistiques',
        href: '/statistics',
        icon: BarChart3,
        roles: ['admin', 'merchant'],
        requiredPermission: 'viewer',
        requiredSpecificPermission: 'viewReports'
      },
      {
        name: 'Logs',
        href: '/logs',
        icon: FileText,
        roles: ['admin']
      }
    ],
    roles: ['admin', 'merchant'],
    collapsedByDefault: true
  },
  {
    id: 'configuration',
    name: 'Configuration',
    icon: Settings,
    items: [
      {
        name: 'Paramètres système',
        href: '/settings',
        icon: Settings,
        roles: ['admin']
      },
      {
        name: 'Pays',
        href: '/countries',
        icon: Globe,
        roles: ['admin']
      },
      {
        name: 'Devises',
        href: '/currencies',
        icon: DollarSign,
        roles: ['admin']
      },
      {
        name: 'Régions',
        href: '/regions',
        icon: Map,
        roles: ['admin']
      },
      {
        name: 'Taux de taxe',
        href: '/tax-rates',
        icon: Percent,
        roles: ['admin']
      },
      {
        name: 'Étiquettes alimentaires',
        href: '/settings/dietary-tags',
        icon: Leaf,
        roles: ['admin']
      },
      {
        name: 'Créneaux horaires',
        href: '/settings/time-slots',
        icon: Clock,
        roles: ['admin', 'merchant']
      }
    ],
    roles: ['admin'],
    collapsedByDefault: true
  }
]

// Helper function to get all navigation items (for backward compatibility)
export const getAllNavigationItems = () => {
  return navigationCategories.flatMap(category => category.items)
}

// Helper function to get categories for a specific user role with RBAC permissions
export const getCategoriesForUser = (userRole: 'admin' | 'merchant' | null) => {
  if (!userRole) return []
  
  return navigationCategories
    .filter(category => category.roles.includes(userRole))
    .map(category => {
      // Filter items based on RBAC permissions
      const filteredItems = category.items.filter(item => {
        // Check role requirement
        if (!item.roles.includes(userRole)) return false
        
        // Check permission level requirement
        if (item.requiredPermission) {
          if (!authService.hasMerchantPermissionLevel(item.requiredPermission)) {
            return false
          }
        }
        
        // Check specific permission requirement
        if (item.requiredSpecificPermission) {
          // Convert string to valid permission type
          const permission = item.requiredSpecificPermission as 'manageUsers' | 'manageProducts' | 'manageOffers' | 'viewReports'
          if (!authService.hasMerchantPermission(permission)) {
            return false
          }
        }
        
        return true
      })
      
      // Return category with filtered items (only if there are items)
      if (filteredItems.length === 0) return null
      
      return {
        ...category,
        items: filteredItems
      }
    })
    .filter(category => category !== null) as NavigationCategory[]
}